import { sameJson } from '@core/utils/shared-utils';

import { kpiFieldName } from '../kpi-names';
import {
  ShorthandResolver,
  ShorthandToken,
  ShorthandValue
} from '../vocabulary/default-operators';
import { FieldPath, FilterExpr, FilterLiteral, makeOp, opArgument, opName } from '../wire';
import { DslError, Token, TokenKind, tokenize } from './lexer';
import {
  DslParser,
  DslResult,
  OPERATOR_START_WORDS,
  isOperatorStart,
  literalFromQuantity
} from './parser';
import {
  printFieldName,
  printFilterOperand,
  printLiteral,
  printPath,
  printSegmentName,
  printStr
} from './printer';

export interface SortQualifier {
  field: string;
  order: 'asc' | 'desc';
  association?: { edge: string; aggregate?: 'min' | 'max' };
}

export type SyntaxRole =
  | 'field'
  | 'operator'
  | 'string'
  | 'number'
  | 'uid'
  | 'edge'
  | 'free'
  | 'error';

export interface SyntaxSpan {
  start: number;
  end: number;
  role: SyntaxRole;
}

/**
 * `pending`: the text ends in a term still being typed (`site_kinds:`, `gross_weight >`), which the
 * bar holds back rather than search for.
 */
export type SearchBarResult = DslResult<SearchBarState> & {
  spans: SyntaxSpan[];
  pending: boolean;
};

export interface SearchBarState {
  expr?: FilterExpr;
  search: string[];
  sort: SortQualifier[];
}

const RESERVED = new Set([
  'and',
  'or',
  'not',
  'all',
  'any',
  'sort',
  ...OPERATOR_START_WORDS
]);
const FREE_TEXT_KINDS: ReadonlySet<TokenKind> = new Set<TokenKind>([
  'word',
  'name',
  'str',
  'int',
  'num',
  'quantity',
  'duration',
  'date',
  'date_time',
  'time'
]);
const NUMBER_KINDS: ReadonlySet<TokenKind> = new Set<TokenKind>([
  'int',
  'num',
  'quantity',
  'duration',
  'date',
  'date_time',
  'time'
]);
const BARE_SEARCH_WORD = /^[\p{L}\p{N}_]+(?:-[\p{L}\p{N}_]+)*$/u;

/** The role of a token no grammar rule coloured: punctuation and keywords read as operators. */
function defaultRole(token: Token): SyntaxRole {
  if (token.kind === 'str') return 'string';
  if (token.kind === 'unreadable') return 'free';
  return NUMBER_KINDS.has(token.kind) ? 'number' : 'operator';
}

/** Booleans, `null`, dates and quantities share the number colour. */
function literalRole(literal: FilterLiteral): SyntaxRole {
  if (literal === 'null') return 'number';
  if ('uid' in literal || 'uid_list' in literal) return 'uid';
  return 'string' in literal ? 'string' : 'number';
}

interface Alternative {
  conditions: FilterExpr[];
  /** First free-text word or sort qualifier typed in this alternative; those belong to the whole bar. */
  loose?: { start: number; end: number };
}

class SearchBarParser extends DslParser {
  private readonly search: string[] = [];
  private readonly sort: SortQualifier[] = [];
  private associationDepth = 0;
  /** Roles the grammar gave tokens; a token missing here takes its `defaultRole`. */
  private readonly roles = new Map<Token, SyntaxSpan[]>();
  /** Set once a term at the end of the text fails only for want of what comes next. */
  pending = false;

  constructor(
    tokens: Token[],
    input: string,
    private readonly resolver: ShorthandResolver
  ) {
    super(tokens, input);
  }

  read(): DslResult<SearchBarState> {
    try {
      const expr = this.parseAlternatives(false);
      return {
        ok: true,
        value: { ...(expr ? { expr } : {}), search: this.search, sort: this.sort }
      };
    } catch (error) {
      if (error instanceof DslError) return { ok: false, error };
      throw error;
    }
  }

  /** The roles of the tokens read so far, with `error`'s range in its own span. */
  spans(error?: DslError): SyntaxSpan[] {
    const overlapsError = (span: SyntaxSpan): boolean =>
      !!error && span.start < error.end && error.start < span.end;
    const spans: SyntaxSpan[] = [];
    for (const token of this.tokens.slice(0, this.pos)) {
      const roles = this.roles.get(token) ?? [
        { start: token.start, end: token.end, role: defaultRole(token) }
      ];
      for (const span of roles.filter((span) => !overlapsError(span))) {
        const last = spans.at(-1);
        if (last?.role === span.role && last.end === span.start) last.end = span.end;
        else spans.push({ ...span });
      }
    }
    if (error && error.end > error.start) {
      spans.push({ start: error.start, end: error.end, role: 'error' });
      spans.sort((a, b) => a.start - b.start);
    }
    return spans;
  }

  private mark(token: Token, role: SyntaxRole): void {
    this.roles.set(token, [{ start: token.start, end: token.end, role }]);
  }

  /**
   * `or`-separated alternatives of space-separated items, up to the end of input or, when `nested`
   * inside parentheses, their `)`. A search word there would silently leave the group it was typed in.
   */
  private parseAlternatives(nested: boolean): FilterExpr | undefined {
    let current: Alternative = { conditions: [] };
    const alternatives = [current];
    let danglingJoiner = false;
    while (!this.atEnd() && !(nested && this.peek()!.kind === ')')) {
      const joiner = this.isWord('and') ? 'and' : this.isWord('or') ? 'or' : null;
      if (joiner) {
        const before = current.conditions.length > 0 || !!current.loose;
        if (!before && nested) this.fail(`a condition before \`${joiner}\``);
        this.pos++;
        const after = !(
          this.atEnd() ||
          this.peek()!.kind === ')' ||
          this.isWord('and') ||
          this.isWord('or')
        );
        if (!after && nested) this.fail(`a condition after \`${joiner}\``);
        if (!before || !after) {
          danglingJoiner = true;
          if (this.atEnd()) this.pending = true;
        }
        if (joiner === 'or') alternatives.push((current = { conditions: [] }));
        continue;
      }
      const start = this.peek()!.start;
      const item = nested ? this.parseItem(true) : this.parseTopItem();
      if (item) current.conditions.push(item);
      else current.loose ??= { start, end: this.peek(-1)!.end };
    }
    // Search words and sort qualifiers apply to the whole bar, so kept beside `or` alternatives they
    // would turn the `or` into an AND of a filter and a word; the whole chain is read as typed.
    if (
      !nested &&
      (danglingJoiner ||
        (alternatives.length > 1 && alternatives.some(({ loose }) => loose)))
    ) {
      this.search.length = 0;
      this.sort.length = 0;
      this.readAsFree(0, this.tokens.length);
      return undefined;
    }
    // Inside a group, a sort qualifier alone as an alternative fails the group the same way.
    const loose =
      alternatives.length > 1
        ? alternatives.find((alt) => alt.conditions.length === 0)?.loose
        : undefined;
    if (loose) {
      throw new DslError(
        loose.start,
        loose.end,
        'a condition in every `or` alternative',
        this.input.slice(loose.start, loose.end)
      );
    }
    const ands = alternatives
      .filter(({ conditions }) => conditions.length > 0)
      .map(
        ({ conditions }): FilterExpr =>
          conditions.length === 1 ? conditions[0] : { and: conditions }
      );
    return ands.length === 0 ? undefined : ands.length === 1 ? ands[0] : { or: ands };
  }

  /**
   * A parenthesised group in the search bar may hold shorthand, not just the DSL. Inside an
   * association the fields belong to the edge's target, which the root resolver cannot see, so groups
   * there take the plain DSL.
   */
  override parseUnary(): FilterExpr {
    if (this.peek()?.kind === '(' && this.associationDepth === 0) {
      this.pos++;
      const inner = this.parseAlternatives(true);
      this.expect(')', '`)` to close the group');
      if (!inner) this.fail('a condition inside the parentheses');
      return inner;
    }
    if (!this.looksLikeAssociation()) return super.parseUnary();
    for (const token of this.tokens.slice(this.pos, this.pos + 5)) this.mark(token, 'edge');
    this.associationDepth++;
    try {
      const association = super.parseUnary();
      this.mark(this.peek(-1)!, 'edge');
      return association;
    } finally {
      this.associationDepth--;
    }
  }

  override parsePath(): FieldPath {
    const from = this.pos;
    const path = super.parsePath();
    this.tokens.slice(from, this.pos).forEach((token, i, tokens) => {
      const isName = token.kind === 'word' || token.kind === 'name';
      if (isName && tokens[i - 1]?.kind !== '[') this.mark(token, 'field');
    });
    return path;
  }

  /** `kpi.`, the name and a key all read as the one field they spell. */
  protected override parseKpiName(): string | null {
    const from = this.pos;
    const name = super.parseKpiName();
    for (const token of this.tokens.slice(from, this.pos)) this.mark(token, 'field');
    return name;
  }

  override parseLiteral(): FilterLiteral {
    const from = this.pos;
    const literal = super.parseLiteral();
    // A list's items and a `self` path colour themselves.
    if (literal === 'null' || !('list' in literal || 'self' in literal)) {
      const role = literalRole(literal);
      for (const token of this.tokens.slice(from, this.pos)) this.mark(token, role);
    }
    return literal;
  }

  /**
   * A top-level item that fails as a filter term, or runs into the next token without a space, is
   * read as free text: one search word, or a group's space-separated words as typed. A failure at
   * the end of the text is a term still being typed. Only a parenthesis that never closes cannot be
   * split off.
   */
  private parseTopItem(): FilterExpr | null {
    const from = this.pos;
    const searchWords = this.search.length;
    const sortQualifiers = this.sort.length;
    try {
      const item = this.parseItem(false);
      if (!this.atEnd() && this.peek()!.start === this.peek(-1)!.end) {
        this.fail('a space after the term');
      }
      return item;
    } catch (error) {
      if (!(error instanceof DslError)) throw error;
      const end = this.freeWordEnd(from);
      if (end === null) {
        if (error.start === this.input.length) throw error;
        this.pos = from;
        const at = this.input.length;
        throw new DslError(at, at, '`)` to close the group', 'end of input');
      }
      if (error.start >= this.input.trimEnd().length) this.pending = true;
      // The failed attempt may have coloured tokens past the word; the next items read them again.
      for (const token of this.tokens.slice(end, this.pos + 1)) this.roles.delete(token);
      this.search.length = searchWords;
      this.sort.length = sortQualifiers;
      if (this.tokens[from].kind === '(') this.readAsFree(from, end);
      else {
        for (const token of this.tokens.slice(from, end)) this.mark(token, 'free');
        this.search.push(
          this.input.slice(this.tokens[from].start, this.tokens[end - 1].end)
        );
      }
      this.pos = end;
      return null;
    }
  }

  /**
   * Tokens `from` to `end` as search words, one per space-separated run, exactly as typed. A run that
   * is one quoted string is the text it quotes, as it is anywhere else in the bar.
   */
  private readAsFree(from: number, end: number): void {
    let start = from;
    for (let i = from; i < end; i++) {
      this.mark(this.tokens[i], 'free');
      if (i + 1 === end || this.tokens[i + 1].start !== this.tokens[i].end) {
        const token = this.tokens[start];
        this.search.push(
          start === i && token.kind === 'str'
            ? token.value
            : this.input.slice(token.start, this.tokens[i].end)
        );
        start = i + 1;
      }
    }
  }

  /**
   * The token index past the free word starting at `from`: the tokens run together, and everything
   * inside a parenthesis it opens. Null when that parenthesis never closes.
   */
  private freeWordEnd(from: number): number | null {
    let depth = 0;
    let i = from;
    for (;;) {
      const kind = this.tokens[i].kind;
      if (kind === '(') depth++;
      else if (kind === ')' && depth > 0) depth--;
      i++;
      if (i === this.tokens.length) return depth === 0 ? i : null;
      if (depth === 0 && this.tokens[i].start !== this.tokens[i - 1].end) return i;
    }
  }

  private parseItem(nested: boolean): FilterExpr | null {
    const token = this.peek()!;
    if (this.isWord('sort') && this.peek(1)?.kind === ':') {
      this.pos += 2;
      this.sort.push(this.parseSortQualifier());
      return null;
    }
    // `-field:v` and `not field:v` both negate a qualifier.
    const negated = token.kind === '-' || (this.isWord('not') && this.isQualifier(1));
    if (this.isQualifier(negated ? 1 : 0)) {
      if (negated) this.pos++;
      const at = this.peek()!;
      const path = this.parsePath();
      this.expect(':', '`:` after the field');
      const predicate = this.parseShorthand(path, at);
      return negated ? { not: predicate } : predicate;
    }
    if (negated) this.fail('a field qualifier after `-`');
    if (this.startsFilter()) return this.parseUnary();
    if (nested) this.fail('a condition');
    if (!FREE_TEXT_KINDS.has(token.kind)) this.fail('a condition or a search word');
    this.search.push(token.value);
    this.mark(token, 'free');
    this.pos++;
    return null;
  }

  /** `<name>(.<name>)*:` starting `offset` tokens ahead. */
  private isQualifier(offset: number): boolean {
    let i = offset;
    for (;;) {
      const kind = this.peek(i)?.kind;
      const kpi = this.kpiNameLength(i);
      if (kpi === 0 && kind !== 'word' && kind !== 'name') return false;
      i += Math.max(kpi, 1);
      if (this.peek(i)?.kind === ':') return true;
      if (this.peek(i)?.kind !== '.') return false;
      i++;
    }
  }

  private startsFilter(): boolean {
    const token = this.peek();
    if (!token) return false;
    if (token.kind === '(') return true;
    if (token.kind !== 'word' && token.kind !== 'name') return false;
    if (this.isWord('not')) return true;
    if ((this.isWord('all') || this.isWord('any')) && this.peek(1)?.kind === '(')
      return true;
    if (this.looksLikeAssociation()) return true;
    let i = 1;
    while (
      this.peek(i)?.kind === '.' &&
      ['word', 'name'].includes(this.peek(i + 1)?.kind ?? '')
    )
      i += 2;
    return this.peek(i)?.kind === '[' || isOperatorStart(this.peek(i));
  }

  private parseShorthand(path: FieldPath, at: Token): FilterExpr {
    const codec = this.resolver.codecFor(path);
    if (!codec)
      throw new DslError(at.start, at.end, 'a field with a default operator', at.value);
    const valueStart = this.peek();
    const from = this.pos;
    const value = this.parseShorthandValue();
    const arg = codec.encode(value);
    if (arg === null) {
      const end = this.peek(-1)?.end ?? this.input.length;
      throw new DslError(
        valueStart!.start,
        end,
        `values the \`${codec.op}\` operator accepts`,
        this.input.slice(valueStart!.start, end)
      );
    }
    const items = this.tokens
      .slice(from, this.pos)
      .filter((token) => token.kind !== ',' && token.kind !== '..');
    const literals: unknown[] =
      value.kind === 'range'
        ? [(arg as { lo: unknown }).lo, (arg as { hi: unknown }).hi]
        : Array.isArray(arg)
          ? arg
          : [arg];
    items.forEach((token, i) => {
      const literal = literals[i];
      // A relative date keyword is a date; tags and `contains` take bare strings.
      const role =
        codec.op === 'relative_date'
          ? 'number'
          : literal === undefined
            ? defaultRole(token)
            : typeof literal === 'string'
              ? 'string'
              : literalRole(literal as FilterLiteral);
      this.mark(token, role);
    });
    return { predicate: { path, op: makeOp(codec.op, arg) } };
  }

  private parseShorthandValue(): ShorthandValue {
    const first = this.shorthandToken();
    if (this.peek()?.kind === '..') {
      this.pos++;
      return { kind: 'range', lo: first, hi: this.shorthandToken() };
    }
    const items = [first];
    while (this.peek()?.kind === ',') {
      this.pos++;
      items.push(this.shorthandToken());
    }
    return { kind: 'list', items };
  }

  private shorthandToken(): ShorthandToken {
    const token = this.peek();
    switch (token?.kind) {
      case 'word':
      case 'name':
      case 'str':
        this.pos++;
        return { raw: token.value, literal: null };
      case 'quantity':
        this.pos++;
        return { raw: token.value, literal: literalFromQuantity(token) };
      case 'int':
      case 'num':
      case 'date':
      case 'date_time':
      case 'duration':
        // The DSL literal reader also refuses impossible dates and malformed durations.
        return { raw: token.value, literal: this.parseLiteral() };
      default:
        this.fail('a value');
    }
  }

  private sortName(): Token {
    const token = this.peek();
    if (token?.kind !== 'word' && token?.kind !== 'name') this.fail('a field name');
    this.pos++;
    return token;
  }

  /**
   * The lexer keeps interior hyphens, so `start-min-desc` arrives as one word and is split here. A
   * backticked field takes its modifiers from a `-word` written directly after the closing backtick.
   */
  private sortFieldAndModifiers(token: Token): { field: string; modifiers: string[] } {
    if (token.kind === 'word') {
      const [field, ...modifiers] = token.value.split('-');
      return { field, modifiers };
    }
    return { field: token.value, modifiers: this.modifiersAfter(token) };
  }

  /** The modifiers of a `-word` written directly after `token`, none when no `-` touches it. */
  private modifiersAfter(token: Token): string[] {
    const dash = this.peek();
    if (dash?.kind !== '-' || dash.start !== token.end) return [];
    this.pos++;
    const word = this.peek();
    if (word?.kind !== 'word' || word.start !== dash.end)
      this.fail('a sort modifier after `-`');
    this.pos++;
    return word.value.split('-');
  }

  /** `kpi.`, or the related entity before `.`, and the field after it, coloured as they read. */
  private sortTarget(): {
    edge?: string;
    field: string;
    modifiers: string[];
    start: number;
  } {
    const kpi = this.kpiNameLength();
    if (kpi === 6) {
      const start = this.peek()!.start;
      const field = this.parseKpiName()!;
      return { field, modifiers: this.modifiersAfter(this.peek(-1)!), start };
    }
    let edge: string | undefined;
    const first = this.peek()!;
    if (kpi === 3) {
      this.mark(first, 'field');
      this.mark(this.peek(1)!, 'field');
      this.pos += 2;
    }
    let token = this.sortName();
    if (kpi === 0 && this.peek()?.kind === '.') {
      this.mark(token, 'edge');
      this.pos++;
      edge = token.value;
      token = this.sortName();
    }
    const { field, modifiers } = this.sortFieldAndModifiers(token);
    // A bare word carries its modifiers after the field name.
    const fieldEnd = token.kind === 'word' ? token.start + field.length : token.end;
    this.roles.set(token, [
      { start: token.start, end: fieldEnd, role: 'field' },
      ...(fieldEnd < token.end
        ? [{ start: fieldEnd, end: token.end, role: 'operator' as const }]
        : [])
    ]);
    return kpi === 3
      ? { field: kpiFieldName(field), modifiers, start: first.start }
      : { edge, field, modifiers, start: token.start };
  }

  private parseSortQualifier(): SortQualifier {
    const { edge, field, modifiers, start } = this.sortTarget();
    let order: 'asc' | 'desc' = 'asc';
    let aggregate: 'min' | 'max' | undefined;
    for (const word of modifiers) {
      if (word === 'asc' || word === 'desc') order = word;
      else if (edge && (word === 'min' || word === 'max')) aggregate = word;
      else {
        const expected = edge ? '`asc`, `desc`, `min` or `max`' : '`asc` or `desc`';
        const end = this.peek(-1)!.end;
        throw new DslError(
          start,
          end,
          `${expected} after the sort field`,
          this.input.slice(start, end)
        );
      }
    }
    if (!edge) return { field, order };
    return { field, order, association: aggregate ? { edge, aggregate } : { edge } };
  }
}

export function parseSearchBar(text: string, resolver: ShorthandResolver): SearchBarResult {
  let tokens: Token[];
  try {
    tokens = tokenize(text, { searchBar: true });
  } catch (error) {
    if (!(error instanceof DslError)) throw error;
    const before = new SearchBarParser(
      tokenize(text.slice(0, error.start), { searchBar: true }),
      text,
      resolver
    );
    before.read();
    return { ok: false, error, spans: before.spans(error), pending: false };
  }
  const parser = new SearchBarParser(tokens, text, resolver);
  const result = parser.read();
  return {
    ...result,
    spans: parser.spans(result.ok ? undefined : result.error),
    pending: result.ok && parser.pending
  };
}

/** The one token the search-bar lexer reads `text` as, or null when it reads anything else or nothing. */
function soleToken(text: string): Token | null {
  try {
    const tokens = tokenize(text, { searchBar: true });
    const [token] = tokens;
    return tokens.length === 1 && token.value === text && token.kind !== 'unreadable'
      ? token
      : null;
  } catch (error) {
    if (error instanceof DslError) return null;
    throw error;
  }
}

/** A bare value must read back as a word, not a number or duration; its hyphens are quoted. */
function printShorthandToken(token: ShorthandToken): string {
  if (token.literal) return printLiteral(token.literal);
  const bare =
    !token.raw.includes('-') &&
    !RESERVED.has(token.raw) &&
    soleToken(token.raw)?.kind === 'word';
  return bare ? token.raw : printStr(token.raw);
}

/** A typed shorthand value as the bar prints it, quoted when it would not read back as that word. */
export function printShorthandWord(raw: string): string {
  return printShorthandToken({ raw, literal: null });
}

function printShorthandValue(value: ShorthandValue): string {
  return value.kind === 'range'
    ? `${printShorthandToken(value.lo)}..${printShorthandToken(value.hi)}`
    : value.items.map(printShorthandToken).join(',');
}

/** `item` as `[-]field:values` when that spelling reads back to exactly the same condition. */
function printShorthand(item: FilterExpr, resolver: ShorthandResolver): string | null {
  const negated = 'not' in item;
  const inner = negated ? item.not : item;
  if (!('predicate' in inner)) return null;
  const { path, op } = inner.predicate;
  const codec = resolver.codecFor(path);
  const arg = opArgument(op);
  if (!codec || arg === null || opName(op) !== codec.op) return null;
  const value = codec.decode(arg);
  if (!value || !sameJson(codec.encode(value), arg)) return null;
  return `${negated ? '-' : ''}${printQualifierPath(path)}:${printShorthandValue(value)}`;
}

/** `sort:` opens a sort qualifier, so a field named `sort` is backticked there. */
export function printQualifierPath(path: FieldPath): string {
  const text = printPath(path);
  return /^sort(?=$|[.[])/.test(text) ? `\`sort\`${text.slice(4)}` : text;
}

function isInfixOr(expr: FilterExpr): expr is { or: FilterExpr[] } {
  return 'or' in expr && expr.or.length >= 2;
}

function printAlternatives(expr: FilterExpr, resolver: ShorthandResolver): string {
  return isInfixOr(expr)
    ? expr.or.map((alternative) => printItems(alternative, resolver)).join(' or ')
    : printItems(expr, resolver);
}

function printItems(expr: FilterExpr, resolver: ShorthandResolver): string {
  const items = 'and' in expr && expr.and.length >= 2 ? expr.and : [expr];
  return items.map((item) => printOperand(item, resolver)).join(' ');
}

/** One space-separated item; nested AND/OR groups keep shorthand inside their parentheses. */
function printOperand(item: FilterExpr, resolver: ShorthandResolver): string {
  const shorthand = printShorthand(item, resolver);
  if (shorthand) return shorthand;
  if (isInfixOr(item)) return `(${printAlternatives(item, resolver)})`;
  if ('and' in item && item.and.length >= 2) return `(${printItems(item, resolver)})`;
  return printFilterOperand(item);
}

/** Only a root sort reads `kpi.` as a KPI, and there a related entity named `kpi` is backticked. */
function printSortQualifier(sort: SortQualifier): string {
  const edge = sort.association?.edge;
  const path =
    edge !== undefined
      ? `${printSegmentName(edge, edge === 'kpi')}.${printSegmentName(sort.field)}`
      : printFieldName(sort.field);
  const aggregate = sort.association?.aggregate ? `-${sort.association.aggregate}` : '';
  return `sort:${path}${aggregate}${sort.order === 'desc' ? '-desc' : ''}`;
}

/** `2024-01-01T10` matches the pattern but is refused as a half-written timestamp, so it must be quoted. */
function printSearchWord(word: string): string {
  const bare =
    BARE_SEARCH_WORD.test(word) && !RESERVED.has(word) && soleToken(word) !== null;
  return bare ? word : printStr(word);
}

export function printSearchBar(state: SearchBarState, resolver: ShorthandResolver): string {
  const parts: string[] = [];
  if (state.expr) {
    const filter = printAlternatives(state.expr, resolver);
    parts.push(state.search.length > 0 && isInfixOr(state.expr) ? `(${filter})` : filter);
  }
  parts.push(...state.search.map(printSearchWord), ...state.sort.map(printSortQualifier));
  return parts.join(' ');
}
